// Pure mapping from the auth service's `reason` (and, for an IdP error, the
// IdP's own `code`) to the words on /auth/error. Kept free of React so every
// row is testable on its own -- see login-error-copy.test.js.
//
// Where each reason comes from (rapid-api-auth, api/ddd/service/auth-provider):
//
//   reasonForIdPError (callback-error.go), the IdP sent the person back with
//   an error; `code` is the IdP's own error code:
//     not_enrolled        AADSTS50105, or interaction_required
//     credential_expired  AADSTS7000222 or invalid_client
//     idp_error           anything else (see IDP_CODES for the codes we name)
//   resolveIdentity (identity.go), our own checks on who the IdP said it was:
//     no_email            the IdP shared no email address
//     domain              the email isn't one of the connection's domains
//     public              a public sign-in button asserted a domain that
//                         has its own connection
//     tenant              the account is from a different directory/tenant
//     email_verified      the IdP says the email isn't verified
//   Callback (service.go):
//     nonce               the response didn't match this sign-in attempt
//   Auth (service.go), before anything is sent to the identity provider:
//     unrouted            the connection is neither a public sign-in method
//                         nor set up for any verified email domain yet
//     testing             the connection is limited to named testers and
//                         this email isn't one of them
//   GetOrganizations (service.go), via /open/organization:
//     sso_not_enabled     no linked organization has SSO in its plan
//     no_organization     linked organizations exist, none accepted this person
//
// originating_email is a finding the server only ever records (never a
// reject), and is listed only so a future change can't land on the generic
// copy by accident.
//
// Every entry: tone (warn|bad), title, body, next (the one thing to do),
// actions (in order; the first is the primary button) and help (show the
// connection's own help text and "Request access" link when it has them).
// `who` is the connection's display name, or a stand-in when unknown.
//
// Actions: password (sign in with your password), set_password,
// retry (start again), different_account.
//
// Require SSO (owner 2026-10-06): when the email's domain answers
// passwordAllowed: false (hooks/domain-status.js), the server will refuse a
// password, so copyFor drops the password actions and uses `nextSso` -- the
// same next step without the password -- for the rows whose `next` mentions
// one. A row left with no action gets "Try again".

import { SCHEDULED_SIGN_IN_MESSAGE } from '../../../../shared/organization-deletion/organization-deletion'

const cap = s => s.charAt(0).toUpperCase() + s.slice(1)

export const REASONS = {
  // Delete organization (Task 25/27): the organization this person would
  // land in is scheduled for deletion and they are not an owner.
  organization_scheduled_for_deletion: {
    tone: 'warn',
    title: 'This organization is scheduled for deletion',
    body: () => SCHEDULED_SIGN_IN_MESSAGE,
    next: () => 'An owner can restore it until the date it is permanently deleted. If you belong to another organization, sign in with that instead.',
    actions: ['different_account'],
    help: false
  },
  not_enrolled: {
    tone: 'warn',
    title: "Your account isn't enrolled yet",
    body: who => `${cap(who)} recognised you, but your organization hasn't given your account access to EducationXR yet.`,
    next: () => "Ask your organization's IT team or EducationXR administrator to add you. If there's a request link below, you can ask for access there.",
    actions: ['retry'],
    help: true
  },
  no_organization: {
    tone: 'warn',
    title: "Your account doesn't have access yet",
    body: who => `${cap(who)} signed you in, but none of the EducationXR organizations that use it has given your account access.`,
    next: () => "Ask your organization's EducationXR administrator to give you access, then try again.",
    actions: ['retry', 'different_account'],
    help: true
  },
  sso_not_enabled: {
    tone: 'warn',
    title: "Single sign-on isn't turned on for your organization",
    body: who => `Your organization's EducationXR plan doesn't include signing in with ${who}, so we couldn't sign you in this way.`,
    next: () => "Sign in with your email and password instead. If you've never set a password, set one first. If you think single sign-on should work, contact your EducationXR administrator.",
    nextSso: () => 'Your organization signs in with single sign-on. Contact your EducationXR administrator to get it working for your account, then try again.',
    actions: ['password', 'set_password'],
    help: false
  },
  credential_expired: {
    tone: 'warn',
    title: 'Sign-in is temporarily unavailable',
    body: who => `The connection between ${who} and EducationXR needs to be renewed by an administrator. We've let them know.`,
    next: () => 'Try again later. If you have an EducationXR password, you can sign in with it now.',
    nextSso: () => 'Try again later.',
    actions: ['password', 'retry'],
    help: false
  },
  unrouted: {
    tone: 'warn',
    title: "This sign-in isn't ready yet",
    body: who => `${cap(who)} isn't set up for anyone's email address yet, so EducationXR can't send you to it.`,
    next: () => "Go back and enter your email: we'll send you to your organization's sign-in if it has one. If you expected this one to work, ask your organization's EducationXR administrator to finish setting it up.",
    actions: ['different_account', 'password'],
    help: false
  },
  testing: {
    tone: 'warn',
    title: 'This sign-in is still being tested',
    body: who => `${cap(who)} is only open to the people testing it right now, and your email isn't one of them.`,
    next: () => "Go back and enter your email: we'll send you to the sign-in that's ready for you, or you can use your password. If you should be testing this one, ask your EducationXR administrator to add you.",
    nextSso: () => "Go back and enter your email: we'll send you to the sign-in that's ready for you. If you should be testing this one, ask your EducationXR administrator to add you.",
    actions: ['different_account', 'password'],
    help: false
  },
  nonce: {
    tone: 'bad',
    title: "We couldn't verify that sign-in",
    body: () => "The response we got back didn't match this sign-in attempt. This can happen if the sign-in page was open for a long time, or was started in another tab.",
    next: () => 'Start again from the sign-in page.',
    actions: ['retry'],
    help: false
  },
  domain: {
    tone: 'bad',
    title: "This account can't sign in this way",
    body: who => `The email address ${who} gave us isn't one this connection is set up for.`,
    next: who => `Sign out of ${who}, then sign in again with your work or organization account. You can also sign in with your EducationXR password.`,
    nextSso: who => `Sign out of ${who}, then sign in again with your work or organization account.`,
    actions: ['different_account', 'password'],
    help: false
  },
  public: {
    tone: 'bad',
    title: 'Your organization has its own sign-in',
    body: who => `The email address ${who} gave us belongs to an organization that signs in through its own connection, so it can't be used here.`,
    next: () => "Go back, enter your email address, and we'll send you to your organization's sign-in.",
    actions: ['different_account'],
    help: false
  },
  tenant: {
    tone: 'bad',
    title: 'This account is from a different organization',
    body: who => `You signed in to ${who} with an account from a different organization (for example, another Microsoft 365 tenant) than the one your organization connected.`,
    next: who => `Sign out of ${who}, then sign in again with your work or organization account.`,
    actions: ['different_account'],
    help: false
  },
  email_verified: {
    tone: 'bad',
    title: "Your email address isn't verified",
    body: who => `${cap(who)} says the email address on this account hasn't been verified, so we can't use it to find your EducationXR account.`,
    next: who => `Verify your email address with ${who} (or ask your IT team to), then try again.`,
    actions: ['retry', 'different_account'],
    help: false
  },
  no_email: {
    tone: 'bad',
    title: "We didn't get an email address",
    body: who => `${cap(who)} signed you in but didn't share an email address, and EducationXR needs one to find your account.`,
    next: () => 'Ask your IT team to share your email address with EducationXR, or sign in with a different account.',
    actions: ['different_account', 'password'],
    help: false
  },
  originating_email: {
    tone: 'bad',
    title: "This account doesn't match the email you entered",
    body: who => `The account you signed in to ${who} with isn't the email address you entered on EducationXR.`,
    next: () => 'Start again and sign in with the same account you entered.',
    actions: ['different_account'],
    help: false
  },
  idp_error: {
    tone: 'bad',
    title: "Sign-in didn't complete",
    body: who => `${cap(who)} couldn't finish signing you in.`,
    next: () => "Try again. If it keeps happening, send the reference below to your organization's IT team or EducationXR administrator.",
    actions: ['retry', 'password'],
    help: false
  }
}

// The IdP error codes (OAuth 2.0 / OpenID Connect) a person can do something
// about, named so idp_error isn't one message for all of them. The server
// passes the IdP's code as-is (callback-error.go); interaction_required is
// already mapped to not_enrolled there.
export const IDP_CODES = {
  access_denied: {
    tone: 'warn',
    title: 'Sign-in was cancelled',
    body: who => `Sign-in was cancelled, or access was declined, at ${who}.`,
    next: () => "Start again when you're ready. If you didn't cancel, your organization may not have given you access to EducationXR yet.",
    actions: ['retry'],
    help: true
  },
  consent_required: {
    tone: 'warn',
    title: "Your organization hasn't approved EducationXR yet",
    body: who => `${cap(who)} needs an administrator to approve EducationXR before people can sign in with it.`,
    next: () => 'Ask your IT administrator to approve EducationXR for your organization, then try again.',
    actions: ['retry', 'password'],
    help: true
  },
  login_required: {
    tone: 'warn',
    title: 'Your sign-in session ended',
    body: who => `${cap(who)} needs you to sign in again.`,
    next: () => 'Start again from the sign-in page.',
    actions: ['retry'],
    help: false
  },
  account_selection_required: {
    tone: 'warn',
    title: 'Choose an account to continue',
    body: who => `${cap(who)} needs you to choose which account to sign in with.`,
    next: () => 'Start again and pick your work or organization account.',
    actions: ['retry'],
    help: false
  },
  temporarily_unavailable: {
    tone: 'warn',
    title: `Sign-in is busy right now`,
    body: who => `${cap(who)} is temporarily unavailable.`,
    next: () => 'Wait a few minutes, then try again.',
    actions: ['retry', 'password'],
    help: false
  },
  server_error: {
    tone: 'warn',
    title: 'Something went wrong on the sign-in side',
    body: who => `${cap(who)} ran into a problem while signing you in.`,
    next: () => "Try again in a few minutes. If it keeps happening, send the reference below to your organization's IT team.",
    actions: ['retry', 'password'],
    help: false
  }
}

// Stand-in when the connection's name isn't known (no provider in the URL,
// or the lookup failed).
export const UNKNOWN_CONNECTION = "your organization's sign-in"

// The actions that lead to a password, which a domain that requires SSO
// never offers.
const PASSWORD_ACTIONS = ['password', 'set_password']

// The row for a reason (or, for an IdP error, its code), and its key.
const entryFor = (reason, code) => {
  let key = REASONS[reason] ? reason : 'idp_error'
  let entry = REASONS[key]

  if (key === 'idp_error' && code && IDP_CODES[code]) {
    key = `idp_error:${code}`
    entry = IDP_CODES[code]
  }

  return { key, entry }
}

// Whether a row reads differently when the domain requires SSO: it offers a
// password action, or its next step mentions one. The page asks the domain
// status only for these.
export const offersPassword = (reason, code) => {
  const { entry } = entryFor(reason, code)
  return !!entry.nextSso || entry.actions.some(action => PASSWORD_ACTIONS.includes(action))
}

// The copy for one error page: the entry for the reason (or, for an IdP
// error, its code), with the connection's name filled in. `key` says which
// row was used, for tests and for the support reference.
//
// passwordAllowed: false (the email's domain requires SSO) drops every
// password action and uses the row's password-free next step; anything else
// is the row as written.
export const copyFor = (reason, code, name, { passwordAllowed = true } = {}) => {
  const who = name || UNKNOWN_CONNECTION
  const { key, entry } = entryFor(reason, code)
  const ssoOnly = passwordAllowed === false

  const actions = ssoOnly ? entry.actions.filter(action => !PASSWORD_ACTIONS.includes(action)) : entry.actions
  const next = ssoOnly && entry.nextSso ? entry.nextSso : entry.next

  return {
    key,
    tone: entry.tone,
    title: entry.title,
    body: entry.body(who),
    next: next(who),
    actions: actions.length ? actions : ['retry'],
    help: entry.help
  }
}
